// View model for the active period "story rail": a progress rail over the week, then the period
// told top to bottom — the reset that started it, what happened inside, now, and when it ends.
import { centsText, decimalCents } from './format';
import {
  bonusResetNote, bonusStatusMeta, formatDuration, formatShortUTC, paymentBackofficeUrl, resetActionView,
  timelineEvents, timelinePct, weekOf,
} from './timeline';

// Colour per action kind. Used in style props (dots, labels, rail), so kept as one constant map;
// bonus is a lighter brand purple so it stays legible on the dark surface.
export const KIND_COLORS = {
  deposit: '#00BD71',
  bonus: '#9D6BFF',
  cashout: '#F04070',
  friday: '#FFD22F',
};

export const KIND_LABELS = {
  deposit: { label: 'Deposit', plural: 'deposits' },
  bonus: { label: 'Bonus', plural: 'bonuses' },
  cashout: { label: 'Cashout', plural: 'cashouts' },
  friday: { label: 'Friday reset', plural: 'Friday resets' },
};

// A cashout always resets the period, so none can be inside it; only deposits and bonuses are totalled.
const TOTAL_KINDS = ['deposit', 'bonus'];

// splitUTC renders a timestamp as { date: 'Thu 15 Oct', time: '23:32' } in UTC.
export const splitUTC = (ts) => {
  if (!ts) return { date: '—', time: '' };
  const d = new Date(ts);
  const date = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).replace(',', '');
  return { date, time: d.toISOString().slice(11, 16) };
};

const sameInstant = (a, b) => Boolean(a && b) && new Date(a).getTime() === new Date(b).getTime();
const isBetween = (ts, from, to) => {
  const t = new Date(ts).getTime();
  return t >= new Date(from).getTime() && t <= new Date(to).getTime();
};

// depositBasisAmount: a deposit as the deposit check measures it (deposits = the check's deposit
// step). One native fiat currency is measured in itself; crypto or several currencies in USD. With
// the CC exception applied only the top currency counts, so other deposits are not counted.
export const depositBasisAmount = (event, deposits) => {
  const basis = deposits?.basis_currency;
  if (!basis) return null;
  const top = deposits.by_currency?.[0]?.currency;
  if (deposits.cc_exception_applied && event.currency !== top) {
    return { counted: false, amount: null, currency: basis };
  }
  if (deposits.basis_native) return { counted: true, amount: event.amount, currency: basis };
  return { counted: true, amount: event.amount_usd, currency: 'USD' };
};

// periodEvents: the transactions inside the active period, oldest first.
export const periodEvents = (period, week) =>
  timelineEvents(weekOf(week, period)).filter((e) => e.active);

// resetTimes: when the opening bonus was activated and finished, or the cashout processed
// (action_info activated_at / finished_at); empty for a Friday reset.
export const resetTimes = (reset) => {
  if (!reset) return null;
  const parts = [];
  if (reset.activatedAt) parts.push(`activated ${formatShortUTC(reset.activatedAt)}`);
  if (reset.finishedAt) parts.push(`${reset.kind === 'cashout' ? 'processed' : 'finished'} ${formatShortUTC(reset.finishedAt)}`);
  return parts.length ? `${parts.join(' · ')} UTC` : null;
};

const startRow = (period) => {
  const reset = resetActionView(period);
  if (!reset) return null;
  return {
    type: 'start',
    key: 'start',
    kind: reset.kind,
    ...splitUTC(reset.time),
    label: reset.kind === 'friday' ? KIND_LABELS.friday.label : reset.label,
    rel: '',
    title: reset.kind === 'friday' ? `${reset.detail} · Fri 00:00 CEST` : reset.detail,
    times: resetTimes(reset),
    badges: reset.status ? [bonusStatusMeta(reset.status)] : [],
    id: reset.id,
    href: reset.href,
    amount: reset.amount,
    currency: reset.currency,
    amountUsd: reset.amountUsd,
  };
};

const eventRow = (e, start, i, deposits) => {
  const badges = e.kind === 'bonus' ? [bonusStatusMeta(e.status), bonusResetNote(e)].filter(Boolean) : [];
  return {
    // deposits: the original amount sits with the ID, the measured one on the right
    basis: e.kind === 'deposit' ? depositBasisAmount(e, deposits) : null,
    type: 'event',
    key: `${e.kind}-${e.created_at}-${i}`,
    kind: e.kind,
    ...splitUTC(e.created_at),
    label: KIND_LABELS[e.kind].label,
    rel: `+${formatDuration(start, e.created_at)}`,
    title: e.kind === 'bonus' ? e.title || null : null,
    badges,
    id: e.action_id === null || e.action_id === undefined ? null : String(e.action_id),
    href: paymentBackofficeUrl(e),
    amount: e.amount,
    currency: e.currency,
    amountUsd: e.amount_usd,
  };
};

// storyRows: the start (reset action), every transaction of the period, now (while the period is
// open) and the period's end. deposits (the check's deposit step) sets how deposits are measured.
export const storyRows = (period, week, now, deposits) => {
  const start = period.period_start;
  const end = period.period_end;
  const rows = [];
  const first = startRow(period);
  if (first) rows.push(first);
  periodEvents(period, week).forEach((e, i) => rows.push(eventRow(e, start, i, deposits)));
  if (now && end && isBetween(now, start, end)) {
    rows.push({
      type: 'now', key: 'now', ...splitUTC(now), label: 'Now', rel: `+${formatDuration(start, now)}`,
      title: `${formatDuration(now, end)} until the period closes`, badges: [],
    });
  }
  if (end) {
    rows.push({
      type: 'end', key: 'end', kind: 'friday', ...splitUTC(end), label: 'Period ends', rel: `+${formatDuration(start, end)}`,
      title: sameInstant(end, weekOf(week, period).end) ? 'Next Friday reset' : null, badges: [],
    });
  }
  return rows;
};

// railView places the period on the week: where it starts, how far now is, and a dot per
// transaction inside it.
export const railView = (period, week, now) => {
  const range = weekOf(week, period);
  const startPct = timelinePct(period.period_start, range.start, range.end) ?? 0;
  const nowPct = now ? timelinePct(now, range.start, range.end) : null;
  const open = nowPct !== null && isBetween(now, period.period_start, period.period_end);
  return {
    startPct,
    nowPct,
    elapsedPct: open ? Math.max(0, nowPct - startPct) : 0,
    startKind: period.action || 'friday',
    dots: periodEvents(period, week).map((e, i) => ({ key: `${e.kind}-${i}`, kind: e.kind, pct: e.pct })),
    weekStart: range.start,
    weekEnd: range.end,
    elapsed: open ? formatDuration(period.period_start, now) : null,
    left: open ? formatDuration(now, period.period_end) : null,
  };
};

// startReason explains why the period starts where it does.
export const startReason = (period) => {
  const { time } = splitUTC(period.action_created_at || period.period_start);
  let reason;
  switch (period.action) {
    case 'bonus': {
      const status = bonusStatusMeta(period.action_status);
      const what = [period.action_title, status?.label.toLowerCase()].filter(Boolean).join(', ');
      reason = `Latest reset bonus in the week${what ? ` (${what})` : ''} resets the start point. Nothing before ${time} counts.`;
      break;
    }
    case 'cashout':
      reason = `Latest cashout in the week resets the start point. Activity before ${time} does not count.`;
      break;
    default:
      reason = 'No cashout or reset bonus this week, so the period runs from the Friday reset.';
  }
  return reason;
};

// periodTotals counts the deposits and bonuses inside the period. Deposits total what the deposit
// check measured, in its currency; bonuses sum in USD.
export const periodTotals = (period, week, deposits) => {
  const events = periodEvents(period, week);
  return TOTAL_KINDS.map((kind) => {
    const list = events.filter((e) => e.kind === kind);
    const n = list.length;
    const text = `${n} ${n === 1 ? KIND_LABELS[kind].label.toLowerCase() : KIND_LABELS[kind].plural}`;
    if (!n) return { kind, text, sum: null, currency: null };
    if (kind === 'deposit' && deposits?.measured && deposits.basis_currency) {
      return { kind, text, sum: deposits.measured, currency: deposits.basis_currency };
    }
    return { kind, text, sum: centsText(list.reduce((s, e) => s + decimalCents(e.amount_usd || '0'), 0)), currency: 'USD' };
  });
};
